import React, {useState, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate, useParams} from 'react-router-dom';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Grid,
    CircularProgress,
    Avatar,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    LinearProgress,
    Alert
} from '@mui/material';
import {
    Edit as EditIcon,
    ArrowBack as ArrowBackIcon,
    Download as DownloadIcon,
    QrCode as QrCodeIcon
} from '@mui/icons-material';
import * as QRCampaignsService from '../../services/qrcampaigns.service';
import {formatCurrency} from '../../utils/Functions';

const QRCampaignDetails = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {id} = useParams();

    const [campaign, setCampaign] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadCampaign();
    }, [id]);

    const loadCampaign = async () => {
        setLoading(true);
        try {
            const response = await QRCampaignsService.getSingleQRCampaign(dispatch, {id: parseInt(id)});
            if (response && response.status && response.result.length > 0) {
                setCampaign(response.result[0]);
            }
        } catch (error) {
            console.error('Error loading campaign:', error);
        }
        setLoading(false);
    };

    const handleBack = () => {
        navigate(process.env.REACT_APP_ADMMIN_PATH + '/manage/qrcampaigns');
    };

    const handleEdit = () => {
        navigate(process.env.REACT_APP_ADMMIN_PATH + `/manage/qrcampaigns/edit/${id}`);
    };

    const handleDownloadQR = () => {
        if (campaign && campaign.QR_IMAGE) {
            const link = document.createElement('a');
            link.href = `data:image/png;base64,${campaign.QR_IMAGE}`;
            link.download = `QR_${campaign.CAMPAIGNNAME}.png`;
            link.click();
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'ACTIVE': return 'success';
            case 'INACTIVE': return 'info';
            case 'EXPIRED': return 'error';
            default: return 'default';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'ACTIVE': return 'Activa';
            case 'INACTIVE': return 'Inactiva';
            case 'EXPIRED': return 'Expirada';
            default: return status;
        }
    };

    const getUsagePercentage = () => {
        if (!campaign || campaign.MAX_USES === 0) return 0;
        return (campaign.CURRENT_USES / campaign.MAX_USES) * 100;
    };

    const isExpired = () => {
        if (!campaign) return false;
        return new Date(campaign.EXPIRATION_DATE) <= new Date();
    };

    const daysUntilExpiration = () => {
        if (!campaign) return 0;
        const expirationDate = new Date(campaign.EXPIRATION_DATE);
        const today = new Date();
        const diffTime = expirationDate - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return Math.max(0, diffDays);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (!campaign) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    No se pudo encontrar la campaña solicitada.
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box display="flex" justifyContent="between" alignItems="center" mb={3}>
                <Box display="flex" alignItems="center" gap={2}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={handleBack}
                        variant="outlined"
                    >
                        Volver
                    </Button>
                    <Typography variant="h4" component="h1">
                        {campaign.CAMPAIGNNAME}
                    </Typography>
                    <Chip
                        label={getStatusLabel(campaign.STATUS)}
                        color={getStatusColor(campaign.STATUS)}
                    />
                </Box>
            </Box>

            <Grid container spacing={3}>
                {/* Campaign Info */}
                <Grid item xs={12} md={8}>
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Información de la Campaña
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="textSecondary">
                                        Nombre de la Campaña
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                        {campaign.CAMPAIGNNAME}
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="textSecondary">
                                        Tipo de Campaña
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                        {campaign.CAMPAIGN_TYPE}
                                    </Typography>
                                </Grid>

                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="textSecondary">
                                        Descripción
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                        {campaign.DESCRIPTION || 'Sin descripción'}
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="textSecondary">
                                        Monto por Donación
                                    </Typography>
                                    <Typography variant="h5" color="primary" gutterBottom>
                                        {formatCurrency(campaign.AMOUNT, 'BOB')}
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="textSecondary">
                                        Fecha de Expiración
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                        {new Date(campaign.EXPIRATION_DATE).toLocaleString('es-ES')}
                                    </Typography>
                                    {!isExpired() && (
                                        <Typography variant="caption" color="info.main">
                                            {daysUntilExpiration()} días restantes
                                        </Typography>
                                    )}
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="textSecondary">
                                        Fecha de Creación
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                        {new Date(campaign.INSERT_DATE).toLocaleString('es-ES')}
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2" color="textSecondary">
                                        ID de QR
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                        {campaign.QR_ID}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Statistics */}
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Estadísticas
                            </Typography>

                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={4}>
                                    <Typography variant="subtitle2" color="textSecondary">
                                        Usos Actuales
                                    </Typography>
                                    <Typography variant="h4" color="primary">
                                        {campaign.CURRENT_USES}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                        de {campaign.MAX_USES === 0 ? 'ilimitados' : campaign.MAX_USES}
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <Typography variant="subtitle2" color="textSecondary">
                                        Total Recaudado
                                    </Typography>
                                    <Typography variant="h4" color="primary">
                                        {formatCurrency(campaign.TOTAL_COLLECTED || 0, 'BOB')}
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <Typography variant="subtitle2" color="textSecondary">
                                        Transacciones Exitosas
                                    </Typography>
                                    <Typography variant="h4" color="info.main">
                                        {campaign.ACTUAL_USES}
                                    </Typography>
                                </Grid>

                                {campaign.MAX_USES > 0 && (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                            Progreso de Usos ({getUsagePercentage().toFixed(1)}%)
                                        </Typography>
                                        <LinearProgress
                                            variant="determinate"
                                            value={getUsagePercentage()}
                                            sx={{ height: 8, borderRadius: 4 }}
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Transactions */}
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Transacciones Recientes
                            </Typography>

                            {campaign.transactions && campaign.transactions.length > 0 ? (
                                <TableContainer component={Paper}>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Fecha</TableCell>
                                                <TableCell>Donante</TableCell>
                                                <TableCell>Email</TableCell>
                                                <TableCell>Monto</TableCell>
                                                <TableCell>Estado</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {campaign.transactions.slice(0, 10).map((transaction, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>
                                                        {new Date(transaction.TRANSACTION_DATE).toLocaleString('es-ES')}
                                                    </TableCell>
                                                    <TableCell>
                                                        {transaction.FIRSTNAME} {transaction.LASTNAME}
                                                    </TableCell>
                                                    <TableCell>
                                                        {transaction.EMAIL}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="h6" color="primary">
                                                            {formatCurrency(transaction.AMOUNT, 'BOB')}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={transaction.STATUSID === 4 ? 'Exitosa' : 'Pendiente'}
                                                            color={transaction.STATUSID === 4 ? 'success' : 'default'}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : (
                                <Box textAlign="center" py={4}>
                                    <Typography variant="body1" color="textSecondary">
                                        No hay transacciones registradas para esta campaña
                                    </Typography>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* QR Code */}
                <Grid item xs={12} md={4}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Código QR
                            </Typography>

                            <Box textAlign="center">
                                {campaign.QR_IMAGE ? (
                                    <>
                                        <Avatar
                                            src={`data:image/png;base64,${campaign.QR_IMAGE}`}
                                            variant="square"
                                            sx={{ width: 250, height: 250, margin: '0 auto', mb: 2 }}
                                        />
                                        <Button
                                            variant="contained"
                                            startIcon={<DownloadIcon />}
                                            onClick={handleDownloadQR}
                                            fullWidth
                                            sx={{ backgroundColor: '#009ce0', mb: 2 }}
                                        >
                                            Descargar QR
                                        </Button>
                                        <Typography variant="body2" color="textSecondary">
                                            Código QR listo para usar en materiales promocionales
                                        </Typography>
                                    </>
                                ) : (
                                    <Box py={4}>
                                        <QrCodeIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
                                        <Typography variant="body2" color="textSecondary">
                                            Código QR no disponible
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Quick Actions */}
                    <Card sx={{ mt: 2 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Acciones Rápidas
                            </Typography>

                            <Box display="flex" flexDirection="column" gap={1}>
                                {campaign.QR_IMAGE && (
                                    <Button
                                        variant="outlined"
                                        startIcon={<DownloadIcon />}
                                        onClick={handleDownloadQR}
                                        fullWidth
                                    >
                                        Descargar QR
                                    </Button>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default QRCampaignDetails;